import { createSelector } from 'reselect';
import { createCachedSelector } from 're-reselect';
import { initialState } from './initialState';
import { get, min, max, map, isEmpty, find, orderBy } from 'lodash';
import * as d3 from 'd3';
import * as constants from './constants';
import { serializeTableData } from 'ui-component/tables/serialization';
import { getDirectionalSurveyDataForSelectedUnit } from 'utils/directional_survey/main2';
import { P_TABLE_COLUMNS } from 'pages/drilling-completion/CementingDesign/schema/table/pressure';
import { SCHEMA_SUMMARY } from 'pages/drilling-completion/CementingDesign/schema/table/ced_ouput_summary';
import { SCHEMA_KEY_INFO } from 'pages/drilling-completion/CementingDesign/schema/table/ced_output_key_info';
import { SCHEMA_PUMP } from 'pages/drilling-completion/CementingDesign/schema/table/ced_output_pump';

const getSchemaWithUom = (schema, uom) => {
  const newSchema = schema.map((col) => {
    const colId = get(col, 'col_id', null);
    const newCol = { ...col };
    if (!isEmpty(colId)) {
      newCol.col_unit = get(uom, `${colId}_uom`, '-');
    }
    return newCol;
  });
  return newSchema;
};

// CURRENT STORE SELECTORS
// ==================================================================================================

export const getStore = (store) => store[constants.NAMESPACE] || initialState;

export const currentLayoutSelector = (store) => getStore(store).currentLayout;

// Project
export const projectIdsSelector = (store) => getStore(store).projectIds;

export const projectsByIdSelector = (store) => getStore(store).projectsById;
export const selectedProjectIdsSelector = (store) => getStore(store).selectedProjectIds;

// Project Input
export const inputGeneralByProjectIdSelector = (store) => getStore(store).inputGeneralByProjectId;
export const inputPressureByProjectIdSelector = (store) => getStore(store).inputPressureByProjectId;
export const inputCementProgramByProjectIdSelector = (store) => getStore(store).inputCementProgramByProjectId;

// Project Output
export const outputKeyInfoByProjectIdSelector = (store) => getStore(store).outputKeyInfoByProjectId;
export const outputSummaryByProjectIdSelector = (store) => getStore(store).outputSummaryByProjectId;
export const outputPumpByProjectIdSelector = (store) => getStore(store).outputPumpByProjectId;

export const selectedCasingIdsSelector = (store) => getStore(store).selectedCasingIds;
export const selectedCementIdsSelector = (store) => getStore(store).selectedCementIds;
export const settingsSelector = (store) => getStore(store).settings;
export const projectsSelector = createSelector(projectIdsSelector, projectsByIdSelector, (ids, byId) => ids.map((id) => byId[id]));

// CURRENT PROJECT SELECTORS
// ==================================================================================================

export const currentProjectIdSelector = createSelector(selectedProjectIdsSelector, (selectedIds) => {
  if (selectedIds.length > 0) {
    return selectedIds[0];
  }
  return null;
});
export const currentCasingIdSelector = createSelector(selectedCasingIdsSelector, (selectedIds) => {
  if (selectedIds.length > 0) {
    return selectedIds[0];
  }
  return null;
});

export const currentCementIdSelector = createSelector(selectedCementIdsSelector, (selectedIds) => {
  if (selectedIds.length > 0) {
    return selectedIds[0];
  }
  return null;
});

// Note: this will limit how many case data will be rendered
export const renderProjectIdsSelector = createSelector(selectedProjectIdsSelector, (selectedProjectIds) => selectedProjectIds.slice(0, 10));
export const currentProjectSelector = createSelector(projectsByIdSelector, currentProjectIdSelector, (byId, id) => byId[id] || {});
export const currentGeneralSelector = createSelector(
  inputGeneralByProjectIdSelector,
  currentProjectIdSelector,
  (byId, id) => byId[id] || {}
);
export const currentPressureSelector = createSelector(
  inputPressureByProjectIdSelector,
  currentProjectIdSelector,
  (byId, id) => byId[id] || {}
);
export const currentInputCementProgramSelector = createSelector(
  inputCementProgramByProjectIdSelector,
  currentProjectIdSelector,
  (byId, id) => byId[id] || {}
);

export const currentSurveyTblSelector = createSelector(currentPressureSelector, (obj) => {
  const data = getDirectionalSurveyDataForSelectedUnit(obj);
  const df = {
    columns: P_TABLE_COLUMNS.map((col) => col.col_id),
    data,
    schema: P_TABLE_COLUMNS
  };
  return serializeTableData(df);
});

export const currentCasingObjectByIdSelector = createSelector(currentInputCementProgramSelector, (cementProgram) => {
  const byId = {};
  const casingPrograms = get(cementProgram, 'casing_program', []);
  casingPrograms.forEach((casingProgram, idx) => {
    const casingId = get(casingProgram, 'id', 'UNKNOWN');
    byId[casingId] = casingProgram;
  });
  return byId;
});

// UI Dropdown
// ==================================================================================================

export const currentCasingOptionSelector = createSelector(currentInputCementProgramSelector, (cementProgram) => {
  const casingPrograms = get(cementProgram, 'casing_program', []);
  const casingOption = [
    {
      id: 'None',
      label: 'No selection'
    }
  ];
  casingPrograms.forEach((casingProgram, idx) => {
    const casingId = get(casingProgram, 'id', 'UNKNOWN');
    const casingType = get(casingProgram, 'casing_type', 'UNKNOWN');
    const setDepth = get(casingProgram, 'set_depth', '');
    const setDepthUom = get(casingProgram, 'set_depth_uom', '');
    const label = `${idx + 1} - ${casingType} ${setDepth} ${setDepthUom}`;
    casingOption.push({
      id: casingId,
      label
    });
  });
  return casingOption;
});

export const currentCasingIdsSelector = createSelector(currentInputCementProgramSelector, (cementProgram) => {
  const casingPrograms = get(cementProgram, 'casing_program', []);
  const casingIds = [];
  casingPrograms.forEach((casingProgram) => {
    const casingId = get(casingProgram, 'id', 'UNKNOWN');
    casingIds.push(casingId);
  });
  return casingIds;
});

export const currentCementTableRowsSelector = createSelector(currentInputCementProgramSelector, (cementProgram) => {
  const casings = get(cementProgram, 'casing_program', []);
  const cements = get(cementProgram, 'cement_program', []);

  const tableRows = [];
  cements.forEach((item) => {
    const casingId = item.casing_id;
    const currentCasing = find(casings, { id: casingId });
    const row = {
      id: item.id,
      casing_id: casingId,
      casing_type: get(currentCasing, 'casing_type', ''),
      set_depth: get(currentCasing, 'set_depth', null),
      set_depth_uom: get(currentCasing, 'set_depth_uom', null),
      cement_name: get(item, 'name', ''),
      designed_depth: get(item, 'designed_depth', null),
      designed_depth_uom: get(item, 'designed_depth_uom', null),
      stage: get(item, 'stage', 1)
    };
    tableRows.push(row);
  });
  // Sorted by set depth
  const sortedTableRows = orderBy(tableRows, ['set_depth'], ['asc']);
  const newTableRows = sortedTableRows.map((item, index) => {
    const obj = { ...item, index: index + 1 };
    return obj;
  });

  return newTableRows;
});

export const currentCementOptionsSelector = createSelector(currentCementTableRowsSelector, (cementRows) => {
  const cementOption = [
    {
      id: 'None',
      label: 'No selection'
    }
  ];
  cementRows.forEach((row, idx) => {
    const cementId = get(row, 'id', 'UNKNOWN');
    // const casingType = get(row, 'casing_type', 'UNKNOWN');
    const cementName = get(row, 'cement_name', '');
    const stage = get(row, 'stage', 1);
    // const designedDepth = get(row, 'designed_depth', '');
    // const designedDepthUom = get(row, 'designed_depth_uom', '');
    const label = `${idx + 1} - ${cementName}`;
    cementOption.push({
      id: cementId,
      label,
      name: cementName,
      stage
    });
  });
  return cementOption;
});

// CURRENT KEY INFO
// ==================================================================================================

export const currentProjectOutputKeyInfoSelector = createSelector(
  outputKeyInfoByProjectIdSelector,
  currentProjectIdSelector,
  (byId, id) => byId[id] || {}
);

export const currentOutputKeyInfoUomSelector = createSelector(currentProjectOutputKeyInfoSelector, (obj) => get(obj, 'key_info_uom', {}));

export const currentOutputKeyInfoSelector = createSelector(currentProjectOutputKeyInfoSelector, (obj) => {
  const uom = get(obj, 'key_info_uom', {});
  const schema = getSchemaWithUom(SCHEMA_KEY_INFO, uom);
  const df = {
    columns: get(obj, 'key_info_cols', []),
    data: get(obj, 'key_info_data_uom', []),
    schema
  };
  return serializeTableData(df);
});

// CURRENT SUMMARY
// ==================================================================================================

export const currentProjectOuputSummarySelector = createSelector(
  outputSummaryByProjectIdSelector,
  currentProjectIdSelector,
  (byId, id) => byId[id] || {}
);

export const currentOutputSummaryUomSelector = createSelector(currentProjectOuputSummarySelector, (obj) => get(obj, 'sum_uom', {}));

export const currentOutputSummarySelector = createSelector(currentProjectOuputSummarySelector, (obj) => {
  const uom = get(obj, 'sum_uom', {});
  const schema = getSchemaWithUom(SCHEMA_SUMMARY, uom);
  const df = {
    columns: get(obj, 'sum_cols', []),
    data: get(obj, 'sum_data_uom', []),
    schema
  };
  return serializeTableData(df);
});

// CURRENT PUMNP
// ==================================================================================================

export const currentProjectOutputPumpSelector = createSelector(
  outputPumpByProjectIdSelector,
  currentProjectIdSelector,
  (byId, id) => byId[id] || {}
);

export const currentOutputPumpUomSelector = createSelector(currentProjectOutputPumpSelector, (obj) => get(obj, 'pump_uom', {}));

export const currentOutputPumpSelector = createSelector(currentProjectOutputPumpSelector, (obj) => {
  const uom = get(obj, 'pump_uom', {});
  const schema = getSchemaWithUom(SCHEMA_PUMP, uom);
  const df = {
    columns: get(obj, 'pump_cols', []),
    data: get(obj, 'pump_data_uom', []),
    schema
  };
  return serializeTableData(df);
});

// ADDITIONAL SELECTORS FOR CHARTS

export const surveyDataSelector = createSelector(currentPressureSelector, (obj) => get(obj, 'p_tbl', []));

export const currentMethodSelector = createSelector(currentPressureSelector, (obj) => get(obj, 'directional_survey_method', undefined));

export const colorAttrSelector = createSelector(currentProjectSelector, (obj) => get(obj, 'survey_chart.color_attr', null));

export const colorStopsSelector = createSelector(currentProjectSelector, (obj) => get(obj, 'survey_chart.color_stops', null));

export const colorAttrMinSelector = createSelector(colorAttrSelector, surveyDataSelector, (colorAttr, rowData) => {
  const attrMin = min(rowData.map((d) => d[colorAttr]));
  return attrMin;
});

export const colorAttrMaxSelector = createSelector(colorAttrSelector, surveyDataSelector, (colorAttr, rowData) => {
  const attrMax = max(rowData.map((d) => d[colorAttr]));
  return attrMax;
});

export const currentCasingSelector = createSelector(
  currentCasingIdSelector,
  currentInputCementProgramSelector,
  (casingId, cementProgram) => {
    /*
  Create a base case casing for sensitivity analysis
  */
    const casingProgram = get(cementProgram, 'casing_program', []);
    const casings = casingProgram.filter((m) => m.id === casingId);
    const casing = get(casings, '[0].casing', null);
    return casing;
  }
);

// COMPARE CASES SELECTOR
// ==========================

export const dataByProjectIdSelector = createCachedSelector(
  projectsByIdSelector,
  inputPressureByProjectIdSelector,
  (state, projectId) => projectId,
  (projectsById, inputPressureByProjectId, projectId) => {
    // Survey Data
    const rowData = get(inputPressureByProjectId, `${projectId}.p_tbl`, []);
    const uom = get(inputPressureByProjectId, `${projectId}.p_tbl_uom`, []);
    const colorAttr = get(projectsById, `${projectId}.survey_chart.color_attr`, 'z');
    const colorStops = get(projectsById, `${projectId}.survey_chart.color_stops`, null);
    const name = get(projectsById, `${projectId}.name`, null);

    // Chart Data
    const colorAttrData = rowData.map((d) => d[colorAttr]);
    const x = rowData.map((d) => d.x);
    const y = rowData.map((d) => d.y);
    const z = rowData.map((d) => d.z);
    const minColorAttr = min(colorAttrData);
    const maxColorAttr = max(colorAttrData);
    const colorData = [];

    if (!isEmpty(colorStops)) {
      const offsets = map(colorStops, 'offset');
      const colors = map(colorStops, 'color');
      const colorScale = d3.scale.linear().domain(offsets).range(colors).interpolate(d3.interpolateRgb);
      colorAttrData.forEach((value) => {
        let color = '#0000ff';
        if (maxColorAttr > minColorAttr && colorScale) {
          const offset = (value - minColorAttr) / (maxColorAttr - minColorAttr);
          color = colorScale(offset);
        }
        colorData.push(color);
      });
    }

    return {
      rowData,
      x,
      y,
      z,
      maxColorAttr,
      minColorAttr,
      name,
      colorData,
      uom
    };
  }
)(
  (state, projectId) => projectId // Cache selectors by projectId
);
